import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CollisionPriority } from '@dnd-kit/abstract';
import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { useDroppable } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import AddOutlined from '@mui/icons-material/AddOutlined';
import ClearOutlined from '@mui/icons-material/ClearOutlined';
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import HighlightAltOutlined from '@mui/icons-material/HighlightAltOutlined';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, IconButton, MuiTabs as Tabs } from '@/shared/ui';

import { Z_INDEX } from '@/config/z-index';
import { ChartProvider } from '@/features/_shared/chart/context/ChartContext';
import { CsvDownloadProvider } from '@/features/_shared/chart/context/CsvTableContext';
import { useAppForm } from '@/features/dashboard/components/AppForm/AppForm/context';
import { getDefinitionForWidget } from '@/features/dashboard/components/apps';
import {
  DrawerStep,
  useDrawerSteps
} from '@/features/dashboard/components/Drawer/context/DrawerStepsContext';
import { useRemoveWidgetContext } from '@/features/dashboard/state/RemoveWidgetContext';
import { MarketWidget, MarketWidgetProvider } from '@/features/markets';
import type { BaseTabType, MarketsComponentType } from '@/features/markets';
import TooltipIconButton from '@/shared/ui/primitives/IconButton';

import { useDashboardEditActions } from '../../../hooks/useDashboardEditActions';
import { Item } from './Item';
import { MoveMenu } from './MoveMenu';

import type { Sx } from '@/shared/types/uiTypes';
import type { ColumnProps } from '../types';

const columnSx: Sx = {
  border: '4px dashed',
  borderColor: 'gx.textDisabled',
  position: 'relative',
  overflow: 'hidden',
  pt: '2px',
  backgroundColor: 'gx.tileDark'
};

const columnHeaderSx: Sx = { display: 'flex', gap: 0.5 };

const tabsFlexSx: Sx = { flexGrow: 1, overflow: 'hidden', display: 'flex' };

const columnTabsSx: Sx = {
  flexGrow: 1,
  minWidth: 0,
  '.name-input': { flexDirection: 'column' },
  '.error-message': { position: 'absolute', top: 0 },
  mr: 0.5
};

const scrollButtonSx: Sx = {
  width: 32,
  minWidth: 32,
  height: 46,
  borderRadius: 0,
  backgroundColor: 'gx.tileLight',
  color: 'gx.textDisabled',
  '&:hover': { color: 'white', backgroundColor: '#161b40' }
};

const itemDropSx: Sx = { flexGrow: 1, position: 'relative', mt: 1.5 };

const columnOverlaySx: Sx = {
  position: 'absolute',
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  backgroundColor: 'rgba(18, 24, 54, 0.80)',
  zIndex: Z_INDEX.draggingGroup
};

const actionsSx: Sx = { display: 'flex', gap: '2px' };

/**
 * Column component representing an app container with tabs.
 * Supports drag-and-drop reordering and contains a preview of the widget.
 */
export const Column = ({
  id,
  index,
  items,
  onRemoveApp,
  onRemoveItem,
  container,
  currentDashboardName,
  currentTabId,
  app,
  onTabCreated,
  preview,
  isDragging
}: ColumnProps) => {
  const handleRef = useRef<HTMLButtonElement | null>(null);
  const tabsRootRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [moveAppMenuAnchor, setMoveAppMenuAnchor] = useState<HTMLElement | null>(null);
  const [canScrollStart, setCanScrollStart] = useState(false);
  const [canScrollEnd, setCanScrollEnd] = useState(false);
  const actions = useDashboardEditActions();

  const { ref } = useSortable({
    id,
    index,
    handle: handleRef,
    type: 'column',
    collisionPriority: CollisionPriority.Low,
    accept: ['column'],
    modifiers: [RestrictToElement.configure({ element: container.current })]
  });

  const { ref: itemDropRef } = useDroppable({
    id: `${id}-item-drop`,
    type: 'column-item-drop',
    accept: ['item', 'singleItem'],
    collisionPriority: CollisionPriority.Low,
    data: { columnId: id }
  });

  const { setDrawerStep, setIsEditMode, setEditingTarget } = useDrawerSteps();
  const { setInitialValues } = useAppForm();

  const tabContent = useMemo((): BaseTabType | undefined => {
    const tabId = items[0]?.id;
    if (!tabId) return undefined;

    const components = preview?.components as MarketsComponentType[] | undefined;
    const appWithTab = components?.find(component => {
      return 'tabs' in component && component.tabs?.some((tab: BaseTabType) => tab.id === tabId);
    });

    return appWithTab?.tabs?.find((tab: BaseTabType) => tab.id === tabId);
  }, [preview, items]);

  const handleEdit = useCallback(
    (tabId: string) => {
      const tab = items.find(t => t.id === tabId);
      if (!tab) return;
      const definition = getDefinitionForWidget(tab);
      const values = definition.fromWidget(tab);
      setIsEditMode(true);
      setEditingTarget({ appId: id, tabId });
      setInitialValues(values);
      setDrawerStep(definition.drawerStep);
    },
    [items, setIsEditMode, setEditingTarget, id, setInitialValues, setDrawerStep]
  );

  const handleRenameItem = useCallback(
    (itemId: string, name: string) => {
      if (!currentTabId) return;
      actions.renameAppTab(currentTabId, id, itemId, name);
    },
    [currentTabId, id, actions]
  );

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollStart(el.scrollLeft > 1);
    setCanScrollEnd(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  // Set up ResizeObserver + scroll listener on the MUI Tabs scroller element.
  useEffect(() => {
    scrollerRef.current =
      tabsRootRef.current?.querySelector<HTMLDivElement>('.MuiTabs-scroller') ?? null;
    updateScrollState();

    const el = scrollerRef.current;
    if (!el) return;

    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    el.addEventListener('scroll', updateScrollState, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', updateScrollState);
    };
  }, [updateScrollState]);

  // Recompute when tabs are added/removed or drag ends.
  useEffect(() => {
    updateScrollState();
  }, [items, isDragging, updateScrollState]);

  const scroll = useCallback((direction: 'left' | 'right') => {
    scrollerRef.current?.scrollBy({ left: direction === 'left' ? -200 : 200, behavior: 'smooth' });
  }, []);

  const { openRemoveModal } = useRemoveWidgetContext();

  return (
    <Box
      ref={ref}
      sx={columnSx}
    >
      <Box sx={columnHeaderSx}>
        <TooltipIconButton
          ref={handleRef}
          sx={{ cursor: 'grab', width: 24 }}
          title='Drag to reorder app'
        >
          <DragIndicatorOutlinedIcon sx={{ fontSize: '21px !important' }} />
        </TooltipIconButton>
        <Box sx={tabsFlexSx}>
          {canScrollStart && (
            <IconButton
              sx={scrollButtonSx}
              onClick={() => {
                scroll('left');
              }}
            >
              <KeyboardArrowLeftIcon />
            </IconButton>
          )}
          <Tabs
            ref={tabsRootRef}
            aria-label='tabs'
            scrollButtons={false}
            sx={columnTabsSx}
            value={false}
            variant='scrollable'
          >
            {items.map((item, idx) => (
              <Item
                key={item.id}
                column={id}
                container={container}
                currentDashboardName={currentDashboardName}
                currentTabId={currentTabId}
                index={idx}
                isSingleItem={items.length === 1}
                item={item}
                onEdit={handleEdit}
                onRemoveItem={onRemoveItem}
                onRename={name => {
                  handleRenameItem(item.id, name);
                }}
                onTabCreated={onTabCreated}
              />
            ))}
          </Tabs>
          {canScrollEnd && (
            <IconButton
              sx={scrollButtonSx}
              onClick={() => {
                scroll('right');
              }}
            >
              <KeyboardArrowRightIcon />
            </IconButton>
          )}
          <TooltipIconButton
            sx={{ width: 32, backgroundColor: 'gx.tileLight' }}
            title='Add new app'
            onClick={() => {
              setIsEditMode(true);
              // appId-only signals "add a new tab inside this widget" (see DrawerStepsContext).
              setEditingTarget({ appId: id });
              setDrawerStep(DrawerStep.SelectApp);
            }}
          >
            <AddOutlined sx={{ fontSize: '14px !important', color: 'gx.textDisabled' }} />
          </TooltipIconButton>
        </Box>
        <Box sx={actionsSx}>
          <TooltipIconButton
            title='Move to ...'
            onClick={e => {
              setMoveAppMenuAnchor(e.currentTarget);
            }}
          >
            <HighlightAltOutlined />
          </TooltipIconButton>
          <TooltipIconButton
            title='Remove app'
            onClick={() => {
              openRemoveModal(() => {
                onRemoveApp(id);
              });
            }}
          >
            <ClearOutlined />
          </TooltipIconButton>
          <MoveMenu
            anchorEl={moveAppMenuAnchor}
            app={app}
            open={Boolean(moveAppMenuAnchor)}
            variant='app'
            onClose={() => {
              setMoveAppMenuAnchor(null);
            }}
            onRemoveFromCurrent={() => {
              onRemoveApp(id);
            }}
          />
        </Box>
      </Box>
      <Box
        ref={itemDropRef}
        sx={itemDropSx}
      >
        {tabContent && (
          <MarketWidgetProvider>
            <ChartProvider>
              <CsvDownloadProvider>
                <MarketWidget tab={tabContent} />
              </CsvDownloadProvider>
            </ChartProvider>
          </MarketWidgetProvider>
        )}
        <Box sx={columnOverlaySx} />
      </Box>
    </Box>
  );
};

export default Column;
